/**
 * Appointments Page
 *
 * List, search, and manage appointments
 */

import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Calendar,
  Search,
  Plus,
  Eye,
  Trash2,
  Loader2,
  AlertCircle,
  Clock,
} from 'lucide-react'

import { useAppointments, useDeleteAppointment } from '@/application/appointments/useAppointments'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { ConfirmDialog } from '@/presentation/components/ui/ConfirmDialog'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/presentation/components/ui/Table'
import { PaginationBar } from '@/presentation/components/ui/Pagination'
import { PAGINATION, APP_ROUTES } from '@/config/constants'
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
  BOOKED_VIA_LABELS,
  type AppointmentWithDetails,
  type AppointmentStatus,
} from '@/domain/appointment/types'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = Object.entries(APPOINTMENT_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}))

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateString: string): string {
  return new Date(dateString + 'T00:00:00').toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatTime(time: string): string {
  return time.substring(0, 5)
}

// =============================================================================
// Components
// =============================================================================

interface AppointmentRowProps {
  appointment: AppointmentWithDetails
  onDelete: (id: string) => void
  isDeleting: boolean
}

function AppointmentRow({ appointment, onDelete, isDeleting }: AppointmentRowProps) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">{appointment.patientName}</span>
          <span className="text-xs text-[var(--foreground-muted)]">
            {appointment.patientDocument}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span>{formatDate(appointment.scheduledDate)}</span>
          <span className="text-xs text-[var(--foreground-muted)] flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTime(appointment.scheduledTime)} - {formatTime(appointment.endTime)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm">{appointment.providerName}</span>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">
          {appointment.modalityCode}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={APPOINTMENT_STATUS_COLORS[appointment.status] as any}>
          {APPOINTMENT_STATUS_LABELS[appointment.status]}
        </Badge>
      </TableCell>
      <TableCell>
        <span className="text-xs text-[var(--foreground-muted)]">
          {BOOKED_VIA_LABELS[appointment.bookedVia]}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Link to={APP_ROUTES.APPOINTMENT_DETAIL(appointment.id)}>
            <Button variant="ghost" size="sm" title="Ver detalles">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(appointment.id)}
            disabled={isDeleting}
            title="Eliminar"
            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Calendar className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">
        No se encontraron citas
      </h3>
      <p className="text-[var(--foreground-muted)] mt-1">
        Intenta ajustar los filtros o agenda una nueva cita
      </p>
      <Link to={APP_ROUTES.APPOINTMENT_NEW} className="mt-4">
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Cita
        </Button>
      </Link>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      <p className="text-[var(--foreground-muted)] mt-4">Cargando citas...</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">Error al cargar</h3>
      <p className="text-[var(--foreground-muted)] mt-1">{message}</p>
      <Button onClick={onRetry} className="mt-4">
        Reintentar
      </Button>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function AppointmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Filters from URL
  const status = searchParams.get('status') || ''
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = PAGINATION.DEFAULT_LIMIT

  // Local state
  const [localStatus, setLocalStatus] = useState(status)
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom)
  const [localDateTo, setLocalDateTo] = useState(dateTo)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [appointmentToDelete, setAppointmentToDelete] = useState<string | null>(null)

  // Build filters
  const filters = useMemo(
    () => ({
      status: (status || undefined) as AppointmentStatus | undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [status, dateFrom, dateTo, page, pageSize]
  )

  // Queries
  const { data, isLoading, isError, error, refetch } = useAppointments(filters)
  const deleteAppointmentMutation = useDeleteAppointment()

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (localStatus) params.set('status', localStatus)
    if (localDateFrom) params.set('dateFrom', localDateFrom)
    if (localDateTo) params.set('dateTo', localDateTo)
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleClearFilters = () => {
    setLocalStatus('')
    setLocalDateFrom('')
    setLocalDateTo('')
    setSearchParams({ page: '1' })
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  const handleDelete = (id: string) => {
    setAppointmentToDelete(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!appointmentToDelete) return
    deleteAppointmentMutation.mutate(appointmentToDelete, {
      onSuccess: () => {
        toast.success('Cita eliminada')
        setDeleteDialogOpen(false)
        setAppointmentToDelete(null)
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al eliminar cita'))
        setDeleteDialogOpen(false)
      },
    })
  }

  const hasFilters = status || dateFrom || dateTo

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Citas</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestiona las citas y el agendamiento
          </p>
        </div>
        <Link to={APP_ROUTES.APPOINTMENT_NEW}>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Cita
          </Button>
        </Link>
      </div>

      {/* Search Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="w-[180px]">
              <Select
                options={STATUS_OPTIONS}
                value={localStatus}
                onChange={(e) => setLocalStatus(e.target.value)}
                placeholder="Estado"
              />
            </div>
            <div className="w-[180px]">
              <Input
                type="date"
                value={localDateFrom}
                onChange={(e) => setLocalDateFrom(e.target.value)}
                placeholder="Desde"
              />
            </div>
            <div className="w-[180px]">
              <Input
                type="date"
                value={localDateTo}
                onChange={(e) => setLocalDateTo(e.target.value)}
                placeholder="Hasta"
              />
            </div>
            <Button type="submit">
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
            {hasFilters && (
              <Button type="button" variant="outline" onClick={handleClearFilters}>
                Limpiar
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState
              message={error?.message || 'Error desconocido'}
              onRetry={() => refetch()}
            />
          ) : !data?.items.length ? (
            <EmptyState />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Fecha / Hora</TableHead>
                    <TableHead>Profesional</TableHead>
                    <TableHead>Modalidad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((appointment) => (
                    <AppointmentRow
                      key={appointment.id}
                      appointment={appointment}
                      onDelete={handleDelete}
                      isDeleting={deleteAppointmentMutation.isPending}
                    />
                  ))}
                </TableBody>
              </Table>

              <PaginationBar
                  currentPage={page}
                  pageSize={pageSize}
                  total={data.total}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar cita"
        description="¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmDelete}
        isLoading={deleteAppointmentMutation.isPending}
      />
    </div>
  )
}
